<template>
  <div class="cashier-container">
    <!-- 装饰背景区 -->
    <div class="header-decoration"></div>

    <div v-if="loading" class="loading-state">
      <van-loading type="spinner" color="#D23B2E" vertical>加载中...</van-loading>
    </div>
    
    <!-- 无订单时的空状态 -->
    <div v-else-if="!orderDetail && !initError" class="empty-state">
      <div class="empty-content">
        <van-empty image="search" description="未获取到相关订单信息" />
        <div class="action-btn-wrapper">
          <div class="ui-btn ui-btn-primary" @click="$router.back()">返回</div>
        </div>
      </div>
    </div>

    <!-- 接口报错时的错误状态 -->
    <div v-else-if="initError" class="empty-state">
      <div class="empty-content">
        <van-empty image="error" :description="errorMsg || '加载失败，请稍后重试'" />
        <div class="action-btn-wrapper action-btn-group">
          <div class="ui-btn ui-btn-gradient" @click="init">重新加载</div>
          <div class="ui-btn ui-btn-primary" @click="goOrderList">去订单列表</div>
        </div>
      </div>
    </div>

    <!-- 主内容区域 -->
    <div v-else class="main-content">
      <!-- 核心支付卡片 -->
      <div class="hero-card">
        <div class="timer-badge" :class="{ 'timer-expired': seconds <= 0 }">
          <van-icon name="clock-o" />
          <span class="timer-text">{{ seconds > 0 ? countTime : '已超时' }}</span>
        </div>
        
        <div class="amount-section">
          <div class="amount-label">订单展示金额</div>
          <div class="amount-value">
            <span class="integer">{{ orderAmount }}</span>
            <span class="unit">{{ cardPointTypeUnit }}</span>
          </div>
          <div class="amount-tip">实际抵扣及补差以下方支付明细为准</div>
        </div>

        <div class="order-info-mini">
          <span class="order-no">订单号: {{ orderNo }}</span>
          <span class="copy-btn" @click="copyOrderNo">复制</span>
        </div>
      </div>

      <!-- 订单明细详情 -->
      <div class="section-card detail-card" v-if="serviceFeeAmount > 0">
        <div class="section-title">订单展示明细</div>
        <div class="detail-list">
          <div class="detail-item">
            <span class="label">订单基础金额</span>
            <span class="value">{{ orderPrice }}{{ cardPointTypeUnit }}</span>
          </div>
          <div class="detail-item">
            <span class="label">
              订单服务费
              <van-icon name="question-o" @click="showServiceFeeTip" />
            </span>
            <span class="value">+{{ serviceFeeAmount }}{{ cardPointTypeUnit }}</span>
          </div>
          <div class="detail-item total-item">
            <span class="label">展示合计</span>
            <span class="value">{{ orderAmount }}{{ cardPointTypeUnit }}</span>
          </div>
        </div>
      </div>

      <!-- 支付方式选择（业务规则：无可用福利卡时不允许支付，包括纯微信支付也不开放） -->
      <div class="section-card payment-select-card">
        <div class="section-title">选择支付方式</div>
        
        <div class="payment-methods">
          <!-- 微信支付 -->
          <div 
            class="pay-method-item" 
            :class="{ 'item-active': wechatChecked, 'item-disabled': !canUseWechat || seconds <= 0 || payList.length === 0 }"
            v-if="canUseWechat"
            @click="onToggleWechat">
            <div class="method-left">
              <div class="icon-wrapper wx-bg">
                <van-image :src="require('../assets/images/wxpay.png')"></van-image>
              </div>
              <div class="method-info">
                <div class="method-name">微信支付</div>
              </div>
            </div>
            <van-checkbox 
              checked-color="#19ad19" 
              v-model="wechatChecked"
              :disabled="!canUseWechat || seconds <= 0 || payList.length === 0"
              @click.stop>
            </van-checkbox>
          </div>

          <!-- 福利卡支付 -->
          <div 
            class="pay-method-item" 
            :class="{ 'item-active': cardChecked, 'item-disabled': seconds <= 0 || payList.length === 0 }"
            @click="showPop">
            <div class="method-left">
              <div class="icon-wrapper card-bg">
                <van-icon name="card" />
              </div>
              <div class="method-info">
                <div class="method-name">
                  福利卡支付
                  <span v-if="payList.length === 0" class="no-card-tip">(暂无可用)</span>
                </div>
              </div>
            </div>
            <div class="method-right">
              <van-checkbox 
                checked-color="#D33B2E" 
                :value="cardChecked"
                :disabled="seconds <= 0 || payList.length === 0"
                @click.stop="onCardCheckboxClick">
              </van-checkbox>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部支付操作栏 -->
    <div class="bottom-bar-wrapper" v-if="orderDetail && !loading && !initError">
      <div class="bottom-bar">
        <div class="price-summary">
          <div class="summary-row">
            <span class="label">当前抵扣:</span>
            <span class="value"><span class="num">{{ payDeductPoint }}</span> {{ deductUnit }}</span>
          </div>
          <div class="summary-row highlight" v-if="canUseWechat && (wechatChecked || parseFloat(remainingMoney) > 0)">
            <span class="label">微信补差:</span>
            <span class="value">¥{{ remainingMoney }}</span>
          </div>
          <!-- 业务规则：无可用福利卡时禁止任何支付（含纯微信），需先有可用福利卡 -->
          <div class="summary-row error-row" v-if="payList.length === 0">
            <span class="error-text">暂无可用福利卡，无法支付</span>
          </div>
          <div class="summary-row error-row" v-else-if="cardChecked && !canPay">
             <span class="error-text">{{ calcErrorMsg || '余额不足' }}</span>
          </div>
        </div>
        <div class="pay-action">
          <button 
            class="pay-submit-btn" 
            :class="{ 'btn-secondary': seconds <= 0 }"
            :disabled="!isPaymentSelected || (!canPay && cardChecked) || seconds <= 0 || payList.length === 0"
            @click="handlePay">
            <template v-if="seconds > 0">
              {{ remainingMoney > 0 ? '确认并支付' : '提交支付' }}
            </template>
            <template v-else>订单已超时</template>
          </button>
        </div>
      </div>
    </div>

    <!-- 福利卡选择弹窗 -->
    <van-action-sheet v-model="showCardSheet" title="请选择福利卡" class="card-selector-sheet">
      <div class="sheet-content">
        <div class="card-list">
          <!-- 列表为空时的友好提示 -->
          <van-empty v-if="sortedPayList.length === 0" image="search" description="暂无可用福利卡" />
          <div v-else
            v-for="(item, index) in sortedPayList" 
            :key="index"
            class="card-tile"
            :class="{ 
              'tile-disabled': isCardDisabled(item), 
              'tile-selected': item.check,
              'tile-highlight': shouldHighlightCard(item)
            }"
            @click="onSelectCard(item)">
            
            <div class="tile-header">
              <div class="card-type-icon">
                <van-icon name="balance-o" />
              </div>
              <div class="card-title-box">
                <div class="card-name">{{ item.cardName }}</div>
                <div class="card-meta">
                  <div class="card-id">No. {{ item.cardNo }}</div>
                  <div class="expire-info" v-if="item.expireTime">有效期至 {{ item.expireTime }}</div>
                </div>
              </div>
              <div class="card-status-check">
                <van-checkbox 
                  v-if="item.cardPointType != CardPointType.MOVIE_TICKET || item.repairDiffPrice !== '2'"
                  checked-color="#D33B2E" 
                  :value="item.check"
                  :disabled="isCardDisabled(item)">
                </van-checkbox>
              </div>
            </div>

            <div class="tile-body">
              <div class="card-info-row">
                <div class="balance-info">
                  <span class="label">可用余额:</span>
                  <span class="value">{{ item.balance }}{{ item.cardPointType == CardPointType.MOVIE_TICKET ? '票' : item.cardPointTypeUnit || cardPointTypeUnit }}</span>
                </div>
                <!-- 已选卡片展示使用额度 或 步进器调整区域 (步进器场景默认显示，其他场景选中后显示) -->
                <div v-if="item.check || (item.cardPointType == CardPointType.MOVIE_TICKET && ((productType === '2' && item.repairDiffPrice === '2') || productType !== '2'))" class="usage-display-box">
                  <!-- 1. 通兑票逻辑: 维持原状 (电影风险补差或非电影订单显示步进器，否则显示数值) -->
                  <template v-if="item.cardPointType == CardPointType.MOVIE_TICKET">
                    <span class="label">本次使用:</span>
                    <template v-if="(productType === '2' && item.repairDiffPrice === '2') || productType !== '2'">
                      <van-stepper 
                        v-model="item.selectVote" 
                        integer 
                        :min="0" 
                        :max="Math.min(item.balance, item.requiredUnits)" 
                        @change="onStepperChange(item)"
                        @click.native.stop
                        button-size="28px"
                        theme="round" />
                    </template>
                    <!-- 2. 点数卡/次卡/金豆卡逻辑: 仅显示数值 (不可调整) -->
                    <template v-else>
                      <span class="usage-value">{{ item.realDeduct || 0 }}票</span>
                    </template>
                  </template>
                  <!-- 3. 其他卡类型: 维持原状 (电影风险补差或非电影订单显示步进器，否则显示数值) -->
                  <template v-else>
                    <span class="label">本次使用:</span>
                    <span class="usage-value">{{ item.realDeduct || 0 }}{{ item.cardPointTypeUnit || cardPointTypeUnit }}</span>
                  </template>
                </div>
              </div>
            </div>

            <div class="tile-footer" v-if="item.disabledReason && isCardDisabled(item)">
              <div class="error-msg">
                <van-icon name="warning-o" /> {{ item.disabledReason }}
              </div>
            </div>
          </div>
        </div>

        <div class="sheet-footer">
          <div class="sheet-summary" :class="{ 'has-error': cardChecked && !canPay }">
            <div class="summary-line">
              当前合计抵扣 <span class="num">{{ payDeductPoint }}</span>{{ deductUnit }}
              <div v-if="remainingRequired > 0 || (canUseWechat && parseFloat(remainingMoney) > 0)" class="summary-hint">
                <template v-if="remainingRequired > 0">当前还差<span class="shortfall-num">{{ remainingRequired }}</span>{{ deductUnit }}</template>
                <template v-if="canUseWechat && parseFloat(remainingMoney) > 0 && remainingRequired > 0">，</template>
                <template v-if="canUseWechat && parseFloat(remainingMoney) > 0">预计微信补差额¥<span class="wx-amount">{{ remainingMoney }}</span></template>
              </div>
            </div>
            <div v-if="cardChecked && !canPay" class="error-tip">
              <van-icon name="warning-o" /> {{ calcErrorMsg || '余额不足' }}
            </div>
          </div>
          <div 
            class="ui-btn ui-btn-primary ui-btn-large" 
            :class="{ 'ui-btn-disabled': !canConfirmCardSelection }"
            @click="canConfirmCardSelection && (showCardSheet = false)">确认选择</div>
        </div>
      </div>
    </van-action-sheet>

    <PutPassWord ref="putPassWord" v-if="showPassInput" :withPassword="true" @closed="showPassInput = false" @success="onPasswordSuccess"></PutPassWord>

    <!-- 服务费说明弹窗 -->
    <CustomDialog
      :visible.sync="showFeeDialog"
      title="服务费说明"
      :message="serviceFeeMessage"
      confirm-text="知道了"
      :showCancel="false"
      @confirm="showFeeDialog = false"
    />
  </div>
</template>

<script>
import CustomDialog from '../components/common/CustomDialog.vue';
import PutPassWord from '../components/cashier/PutPassWord.vue';
import { Toast, Dialog } from 'vant';
import { calculateDeduction, CardPointType, calculateInitialSelectVote, isFixedUnitCardType, wxAmountToPayNumber } from '@/utils/paymentCalculator';

export default {
  name: 'UnifiedCashier',
  components: {
    CustomDialog,
    PutPassWord
  },
  data() {
    return {
      orderNo: '',
      orderDetail: null,
      payList: [],
      wechatChecked: false,
      cardChecked: false,
      showCardSheet: false,
      showPassInput: false,
      showFeeDialog: false,
      
      // 订单金额信息
      orderAmount: 0, // 后端定义的应付总额 (Total)
      orderPrice: 0, // 不包含服务费的订单金额 (Net)
      serviceFeeAmount: 0,
      serviceFeeRate: 0,
      
      // 支付相关
      payDeductPoint: 0, // 福利卡抵扣的点数/票数
      remainingMoney: 0, // 微信需付金额
      openId: '', // 微信实时openId (通过snsapi_base获取)
      
      // 倒计时
      seconds: 600,
      countTime: '00:00',
      timer: null,
      
      // 业务类型
      productType: '', // 商品类型
      orderCardPointType: '',
      cardPointTypeUnit: '点',
      deductUnit: '点', // 合计抵扣单位 (跟随选中卡片)
      
      currentPayGroup: '', // 当前选中的卡组标识
      sortedSeatPrices: [], // 座位价格排序列表 (纯数字数组)
      loading: false,
      initError: false,
      errorMsg: '',
      CardPointType,

      // 计算结果状态
      canPay: true, 
      calcErrorMsg: '',
      remainingRequired: 0 // 还需补的点数/票数（用于“还需补X点/票”提示）
    }
  },
  computed: {
    sortedPayList() {
      // 排序规则: 1. 可用优先 2. 支付分组 3. 过期时间升序
      return [...this.payList].sort((a, b) => {
        // 1. 可用卡片优先 (available=true 排前)
        const aAvailable = a.available !== false ? 1 : 0;
        const bAvailable = b.available !== false ? 1 : 0;
        if (aAvailable !== bAvailable) return bAvailable - aAvailable;
        
        // 2. 支付分组
        const groupCompare = (a.payGroup || '').localeCompare(b.payGroup || '');
        if (groupCompare !== 0) return groupCompare;
        
        // 3. 过期时间升序 (即将过期的排前)
        const aTime = a.expireTime ? new Date(a.expireTime).getTime() : Infinity;
        const bTime = b.expireTime ? new Date(b.expireTime).getTime() : Infinity;
        return aTime - bTime;
      });
    },
    isPaymentSelected() {
      return this.cardChecked || this.wechatChecked;
    },
    canUseWechat() {
      // 次卡/金豆卡当前不支持补差价；已选卡时以当前卡组判断，未选卡时看列表是否存在支持补差价的卡。
      const selectedCards = this.payList.filter(item => item.check);
      if (selectedCards.length > 0) {
        if (isFixedUnitCardType(selectedCards[0].cardPointType)) return false;
        return selectedCards[0].repairDiffPrice !== '0';
      }

      if (this.payList.length === 0) return false;
      const referenceType = this.orderCardPointType || this.payList[0]?.cardPointType;
      if (isFixedUnitCardType(referenceType)) return false;
      // 筛选出的福利卡全部不支持补差价时，不展示微信补差额
      return this.payList.some(item => item.repairDiffPrice !== '0');
    },
    // 已选卡，且余额足够或不支持补差以外的组合已可支付，才能确认
    canConfirmCardSelection() {
      return this.cardChecked && this.canPay;
    },
    serviceFeeMessage() {
      const rate = this.serviceFeeRate ? (this.serviceFeeRate * 100).toFixed(2) : 0;
      return `<p>服务费计算规则：</p>
              <p>• 服务费 = 订单金额 × ${rate}%</p>
              <p style='color: #999;font-size: 0.8rem;margin-top: 1rem;'>注：具体收费以实际支付点数为准</p>`;
    }
  },
  watch: {
    cardChecked(val) {
      if (!val) {
        // 取消勾选福利卡支付时，清除所有已选卡片
        this.payList.forEach(item => {
          item.check = false;
          if (item.cardPointType == CardPointType.MOVIE_TICKET) item.selectVote = 0;
        });
        this.currentPayGroup = '';
        this.recalculate();
      }
    }
  },
  async created() {
    this.orderNo = this.$route.query.orderNo || '';

    // 微信浏览器环境下，处理微信授权
    if (this.isWechatBrowser()) {
      const appId = process.env.VUE_APP_WX_APPID;
      const openIdCacheKey = `wx_pay_openid_${appId}`;
      const cachedOpenId = localStorage.getItem(openIdCacheKey);
      const urlParams = new URLSearchParams(window.location.search);
      const wxCode = urlParams.get('code');
      
      if (wxCode) {
        this.loading = true; // 获取 openid 期间保持 loading，掩盖闪烁
        try {
          const res = await this.$api.codeCallback(wxCode);
          if (res.code === 0 && res.data && res.data.openid) {
            localStorage.setItem(openIdCacheKey, res.data.openid);
            this.openId = res.data.openid;
          }
        } catch (e) {
          console.error('获取微信openId失败', e);
        }
        
        // 清理URL中的code参数，防止刷新抛错
        let pathAndHash = window.location.pathname + window.location.hash;
        let newParams = new URLSearchParams(window.location.search);
        newParams.delete('code');
        newParams.delete('state');
        let cleanUrl = pathAndHash + (newParams.toString() ? '?' + newParams.toString() : '');
        if (window.history.replaceState) {
          window.history.replaceState({}, '', cleanUrl); // 通过 replace 干净后退返回上一页
        }
        
        this.loading = false;
        this.init();
      } else if (!cachedOpenId) {
        // 无 openid，无 code，页面拦截加载逻辑，先去跳微信换 code
        this.loading = true; 
        const currentFullUrl = window.location.origin + window.location.pathname + window.location.search + window.location.hash;
        const redirectUri = encodeURIComponent(currentFullUrl);
        const authUrl = `https://open.weixin.qq.com/connect/oauth2/authorize?appid=${appId}&redirect_uri=${redirectUri}&response_type=code&scope=snsapi_base&state=global#wechat_redirect`;
        window.location.replace(authUrl);
        return; // 中断本组件 created 钩子后续逻辑，彻底防止渲染收银台内容后又跳走
      } else {
        // 缓存有 openId，直接正常初始化页面
        this.openId = cachedOpenId;
        this.init();
      }
    } else {
      // 非微信浏览器
      this.init();
    }
  },
  mounted() {
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
  methods: {
    isWechatBrowser() {
      const ua = navigator.userAgent.toLowerCase();
      return ua.includes('micromessenger');
    },

    goOrderList() {
      this.$router.replace({ path: '/AllOrder' });
    },

    async init() {
      if (!this.orderNo || this.loading) return;
      // 1. 初始化状态，清理旧数据和计时器
      this.loading = true;
      this.initError = false;
      this.errorMsg = '';
      this.orderDetail = null;
      this.payList = [];
      this.cardChecked = false;
      this.wechatChecked = false;
      this.payDeductPoint = 0;
      this.remainingMoney = 0;
      this.canPay = true;
      this.calcErrorMsg = '';
      this.currentPayGroup = ''; // 重置支付组标识

      if (this.timer) {
        clearInterval(this.timer);
      }

      try {
        // 1. 调用统一获取支付信息接口
        const res = await this.$api.initCashier(this.orderNo);
        if (res.code === 0 && res.data) {
          const data = res.data;
          this.orderDetail = data; // 存一份原始数据
          
          // 2. 映射订单基本信息
          this.orderAmount = data.orderAmount || 0;
          this.orderPrice = data.orderPrice || data.orderAmount || 0;
          this.serviceFeeAmount = data.serviceFeeAmount || 0;
          this.serviceFeeRate = data.serviceFeeRate || 0;
          this.productType = data.productType;
          this.orderCardPointType = data.cardPointType || '';
          this.cardPointTypeUnit = data.cardPointTypeUnit || '点';

          // 3. 处理倒计时
          if (data.orderCloseTime) {
            const expire = this.$moment(data.orderCloseTime);
            this.seconds = expire.diff(this.$moment(), 'seconds');
          } else if (data.createAt) {
            const create = this.$moment(data.createAt);
            const now = this.$moment();
            this.seconds = 600 - now.diff(create, 'seconds');
          } else {
            this.seconds = 600;
          }

          // 4. 处理福利卡列表
          this.payList = (data.availableCards || []).map(item => ({
            ...item,
            check: false,
            checkTime: 0, // 记录选中时间
            selectVote: 0, // 用于存储抵扣数量/点数
            disabledReason: item.disabledReason
          }));
          
          this.sortedSeatPrices = data.sortedSeatPrices || [];
          
          if (!data.cardPointTypeUnit && this.payList.length > 0) {
            this.cardPointTypeUnit = this.payList[0].cardPointTypeUnit;
          }

          // 5. 初次计算抵扣与补差价（含未选卡时按福利卡最大 wxPoints 换算）
          this.recalculate();

          // 6. 开启倒计时
          this.startCountdown();
        } else {
          this.initError = true;
          this.errorMsg = res.msg || '获取订单信息失败';
          if (res.msg) Toast(res.msg);
        }
      } catch (e) {
        console.error(e);
        this.initError = true;
        this.errorMsg = '初始化失败，请检查网络或订单状态';
        Toast('初始化失败');
      } finally {
        this.loading = false;
      }
    },

    isCardDisabled(item) {
      if (Number(item.balance || 0) <= 0) return true;
      // 不支持补差价且卡上仍有余额：后端旧数据可能仍带「可用余额不足」，此时允许勾选
      if (item.disabledReason && !this.isSoftInsufficient(item)) return true;
      // 互斥逻辑: 必须是同一分组
      if (this.currentPayGroup && item.payGroup !== this.currentPayGroup) return true;
      return false;
    },

    // 旧接口可能仍对「同组不够」返回该文案，此时卡上有余额则允许勾选
    isSoftInsufficient(item) {
      return item.repairDiffPrice === '0'
        && Number(item.balance || 0) > 0
        && item.disabledReason === '福利卡可用余额不足';
    },

    onSelectCard(item) {
      if (this.isCardDisabled(item)) {
        if (item.disabledReason && !this.isSoftInsufficient(item)) Toast(item.disabledReason);
        else if (Number(item.balance || 0) <= 0) Toast('余额不足');
        else Toast('所选卡不能合并支付');
        return;
      }

      // 单笔订单最多允许五张卡支付（与旧版收银台一致）。若用户持有>5张卡（不支持补差价/普通补差价）且订单所需票数需多于5张卡才能凑齐，则无法支付；该场景在实际业务中应不存在。
      const selectedCount = this.payList.filter(c => c.check).length;
      if (!item.check && selectedCount >= 5) {
        Toast('该笔订单最多允许五张卡支付');
        return;
      }

      // 1. 切换选中状态并记录时间
      item.check = !item.check;
      item.checkTime = item.check ? Date.now() : 0;

      // 2. 计算初始 selectVote（逻辑统一在 paymentCalculator.js 中）
      if (item.cardPointType === CardPointType.MOVIE_TICKET) {
        if (item.check) {
          // 选中时：计算初始票数
          item.selectVote = calculateInitialSelectVote(
            item,
            this.payList,
            { productType: this.productType },
            true
          );
        } else {
          // 取消选中时：清零并重新分配其他选中卡片的票数
          item.selectVote = 0;
          this.redistributeSelectVotes(item);
        }
      }

      // 3. 维护当前选中的卡组标识 (用于互斥校验)
      if (item.check) {
        this.currentPayGroup = item.payGroup;
      } else {
        // 如果没有勾选的卡了，重置组标识
        if (!this.payList.some(c => c.check)) {
          this.currentPayGroup = '';
        }
      }
      
      this.cardChecked = this.payList.some(c => c.check);
      this.recalculate();
    },

    /**
     * 重新分配剩余选中卡片的selectVote
     * 当取消选中一张卡时，需要重新计算剩余卡片的票数分配
     */
    redistributeSelectVotes(uncheckedCard) {
      if (this.productType !== '2') return; // 只处理电影订单
      
      // 获取同组仍选中的卡片（按选中时间排序）
      const remainingCards = this.payList
        .filter(c => c.check && c.payGroup === uncheckedCard.payGroup && c.cardPointType === CardPointType.MOVIE_TICKET)
        .sort((a, b) => (a.checkTime || 0) - (b.checkTime || 0));
      
      if (remainingCards.length === 0) return;
      
      // 获取需求票数
      const requiredUnits = Number(remainingCards[0].requiredUnits) || 0;
      
      // 重新分配票数（按选中顺序）
      let remainingNeeded = requiredUnits;
      for (const card of remainingCards) {
        const balance = Number(card.balance) || 0;
        const newVote = Math.min(balance, remainingNeeded);
        card.selectVote = newVote;
        remainingNeeded = Math.max(0, remainingNeeded - newVote);
      }
    },

    /**
     * 判断卡片是否需要高亮提示（票数不足时引导继续选同组卡）
     */
    shouldHighlightCard(item) {
      // 只处理电影订单通兑票
      if (this.productType !== '2' || item.cardPointType !== CardPointType.MOVIE_TICKET) {
        return false;
      }
      
      // 只对未选中的同组卡片做高亮提示
      if (item.check || this.isCardDisabled(item)) {
        return false;
      }
      
      // 检查是否有已选中的同组卡片
      const selectedSameGroup = this.payList.filter(c => 
        c.check && c.payGroup === item.payGroup
      );
      
      if (selectedSameGroup.length === 0) {
        return false;
      }
      
      // 只对普通补差价(1)和不支持补差价(0)类型做高亮提示
      const firstSelected = selectedSameGroup[0];
      if (firstSelected.repairDiffPrice === '2') {
        return false; // 风险补差价不需要高亮提示
      }
      
      // 计算当前已选票数是否足够
      const totalSelected = selectedSameGroup.reduce((sum, c) => sum + (Number(c.selectVote) || 0), 0);
      const requiredUnits = Number(firstSelected.requiredUnits) || 0;
      
      // 票数不足时，高亮提示其他同组卡片
      return totalSelected < requiredUnits;
    },

    onStepperChange(item) {
      item.check = item.selectVote > 0;
      // 维护时间戳，确保手动调整票数的卡片有选中顺序
      if (item.check && !item.checkTime) {
        item.checkTime = Date.now();
      } else if (!item.check) {
        item.checkTime = 0;
      }

      if (item.check) {
        this.currentPayGroup = item.payGroup;
      } else {
        if (!this.payList.some(c => c.check)) this.currentPayGroup = '';
      }
      this.cardChecked = this.payList.some(c => c.check);
      this.recalculate();
    },

    recalculate() {
      // 0. 重置所有卡片的实际抵扣值
      this.payList.forEach(c => { c.realDeduct = 0; });

      // 按选中时间排序，确保“先选先抵”对应的业务逻辑 (SelectedCards 传给计算器时必须有序)
      const selectedCards = this.payList
        .filter(c => c.check)
        .sort((a, b) => (a.checkTime || 0) - (b.checkTime || 0));

      const res = calculateDeduction(selectedCards, {
        orderAmount: this.orderAmount,
        productType: this.productType,
        sortedSeatPrices: this.sortedSeatPrices,
        orderCardPointType: this.orderCardPointType
      }, this.payList);

      this.payDeductPoint = res.totalDeduct;
      this.remainingMoney = res.wxAmount;
      this.deductUnit = res.deductUnit; // 更新抵扣单位
      this.canPay = res.canPay;
      this.calcErrorMsg = res.errorMsg;
      this.remainingRequired = res.remainingRequired != null ? res.remainingRequired : 0;

      // 固定单位订单（次卡/金豆卡）或不支持补差价的卡组，统一关闭微信勾选，避免残留旧状态。
      if (!this.canUseWechat && this.wechatChecked) {
        this.wechatChecked = false;
      }

      // 页面若已判定不可用微信补差，则清空展示层的补差金额，避免出现“无微信入口却显示补差”的错觉。
      if (!this.canUseWechat) {
        this.remainingMoney = '0.00';
      }

      // 如果完全覆盖，取消微信勾选
      if (this.remainingMoney == 0 && this.wechatChecked && this.payDeductPoint >= this.orderAmount) {
         this.wechatChecked = false;
      }
      

    },

    onToggleWechat() {
      if (this.seconds <= 0) return;
      if (!this.canUseWechat) {
        Toast('当前业务暂不支持微信联合支付');
        return;
      }
      this.wechatChecked = !this.wechatChecked;
    },

    onCardCheckboxClick() {
      if (this.cardChecked) {
        // 如果已经选中（有选中的卡），点击复选框则清空所有已选卡片
        this.payList.forEach(item => {
          item.check = false;
          if (item.cardPointType == CardPointType.MOVIE_TICKET) item.selectVote = 0;
        });
        this.currentPayGroup = '';
        this.cardChecked = false;
        this.recalculate();
      } else {
        // 如果未选中，点击复选框则弹出选择框
        this.showPop();
      }
    },

    showPop() {
      if (this.seconds <= 0) return;
      this.showCardSheet = true;
    },

    startCountdown() {
      this.timer = setInterval(() => {
        if (this.seconds <= 0) {
          this.countTime = '已超时';
          clearInterval(this.timer);
          return;
        }
        this.seconds--;
        const m = Math.floor(this.seconds / 60);
        const s = this.seconds % 60;
        this.countTime = `${m < 10 ? '0' + m : m}:${s < 10 ? '0' + s : s}`;
      }, 1000);
    },

    showServiceFeeTip() {
      this.showFeeDialog = true;
    },

    async handlePay() {
      if (this.seconds <= 0) {
        Toast('订单已超时，请重新下单');
        return;
      }
      if (this.payList.length === 0) {
        Toast('暂无可用福利卡，无法支付');
        return;
      }
      if (!this.isPaymentSelected) {
        Toast('请选择支付方式');
        return;
      }
      
      // 核心校验: 阻止不允许支付的情况 (如不支持补差价但余额不足)
      if (this.cardChecked && this.canPay === false) {
        Toast(this.calcErrorMsg || '当前组合不支持支付');
        return;
      }

      // 补差说明提示
      if (this.remainingMoney > 0 && !this.wechatChecked) {
        Dialog.confirm({
          title: '提示',
          message: '余额不足，差额部分将使用微信支付，是否继续？',
        }).then(() => {
          this.wechatChecked = true;
          this.submitOrder();
        });
        return;
      }

      this.submitOrder();
    },

    async submitOrder() {
      // 检查是否需要支付密码 (如果有卡片消费)
      if (this.cardChecked) {
        const res = await this.$api.pwdCheck();
        if (!res.data) {
          Dialog.confirm({
            title: '提示',
            message: '您尚未设置支付密码，暂不能支付',
            confirmButtonText: '去设置'
          }).then(() => {
            this.$router.push('/SetPassWord');
          });
          return;
        }
        // 展示密码弹窗（with-password模式：密码不在内部校验，直接通过事件返回）
        this.showPassInput = true;
        this.$nextTick(() => {
          this.$refs.putPassWord.showKeyBoard();
        });
      } else {
        // 纯微信支付
        this.submitToBackend(null);
      }
    },

    // 密码输入成功回调（with-password模式下 password为6位密码字符串）
    onPasswordSuccess(password) {
      this.showPassInput = false;
      this.submitToBackend(password);
    },

    async submitToBackend(password) {
      const hasWxPay = this.remainingMoney > 0;

      if (hasWxPay && !this.openId) {
        if (this.isWechatBrowser()) {
          Toast('微信授权失败，请刷新页面重新授权');
          return;
        }
        Toast('请在微信浏览器中进行微信支付');
        return;
      }

      Toast.loading({ message: '提交中...', forbidClick: true });

      // 按卡类型显式设置 selectVote：通兑票用选择票数，其他卡用余额（后端按此做校验）
      const cardSelections = this.payList
        .filter(c => c.check)
        .sort((a, b) => (a.checkTime || 0) - (b.checkTime || 0))
        .map(c => ({
          cardId: c.cardId,
          selectVote: c.cardPointType === this.CardPointType.MOVIE_TICKET
            ? (Number(c.selectVote) || 0)
            : (Number(c.balance) || 0)
        }));

      const postData = {
        orderNo: this.orderNo,
        cardSelections: cardSelections,
        wxAmount: hasWxPay ? wxAmountToPayNumber(this.remainingMoney) : null,
        payPassword: password || null,
        openId: hasWxPay ? this.openId : null
      };

      try {
        const res = await this.$api.submitUnifiedPay(postData);
        Toast.clear();

        if (res.code === 0 && res.data) {
          if (res.data.needWxPay && res.data.wxPayParams) {
            this.invokeWechatPay(res.data.wxPayParams);
          } else {
            Toast.success(res.data.message || '支付成功');
            this.goSuccess();
          }
        } else {
          Toast(res.msg || '支付失败');
        }
      } catch (e) {
        Toast.clear();
        Toast('提交失败，请重试');
      }
    },


    invokeWechatPay(params) {
      const _this = this;
      const doInvoke = () => {
        WeixinJSBridge.invoke('getBrandWCPayRequest', {
          appId: params.appId,
          timeStamp: params.timeStamp,
          nonceStr: params.nonceStr,
          package: params.package,
          signType: params.signType,
          paySign: params.paySign
        }, function (res) {
          Toast.clear();
          if (res.err_msg === 'get_brand_wcpay_request:ok') {
            Toast.success('支付成功');
            _this.goSuccess();
          } else {
            // 用户取消或未完成微信支付：释放后端支付会话锁，便于用户再次发起支付
            _this.$api.cancelPaySession(_this.orderNo).catch(() => {});
            Toast('订单支付未完成');
            _this.$router.replace({ path: '/AllOrder', query: { active: 1 } });
          }
        });
      };

      // 兼容 WeixinJSBridge 未加载完成的情况
      if (typeof WeixinJSBridge === 'undefined') {
        document.addEventListener('WeixinJSBridgeReady', doInvoke, false);
      } else {
        doInvoke();
      }
    },

    goSuccess() {
      this.$router.replace({
        // 电影订单跳转到订单详情，其他订单跳转到卡券订单详情
        path: this.productType === '2' ? '/OrderDetail' : '/CakaOrderDetail',
        // 标记为“从统一收银台支付成功后进入”，用于详情页拦截微信浏览器原生返回。
        query: { orderNo: this.orderNo, fromUnifiedCashier: '1' }
      });
    },

    copyOrderNo() {
      const input = document.createElement('input');
      input.setAttribute('readonly', 'readonly');
      input.setAttribute('value', this.orderNo);
      document.body.appendChild(input);
      input.select();
      if (document.execCommand('copy')) {
        document.execCommand('copy');
        Toast('已复制订单号');
      }
      document.body.removeChild(input);
    }
  }
}
</script>

<style lang="scss" scoped>
// ========== CSS 变量定义 ==========
:root {
  // 主题色
  --color-primary: #e74c3c;
  --color-primary-dark: #c0392b;
  --color-primary-light: #fb8888;
  
  // 警告/高亮色
  --color-warning: #ff6b35;
  --color-warning-light: #ff976a;
  
  // 文字色
  --color-text-primary: #2d3436;
  --color-text-secondary: #636e72;
  --color-text-disabled: #b2bec3;
  
  // 背景色
  --color-bg-page: #f6f8fb;
  --color-bg-card: #fff;
  --color-bg-disabled: #dfe6e9;
  
  // 间距
  --spacing-xs: 0.4rem;
  --spacing-sm: 0.8rem;
  --spacing-md: 1.2rem;
  --spacing-lg: 1.6rem;
  
  // 圆角
  --radius-sm: 0.8rem;
  --radius-md: 1.2rem;
  --radius-lg: 1.5rem;
  --radius-xl: 2rem;
}

.cashier-container {
  background-color: var(--color-bg-page, #f6f8fb);
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  padding-bottom: constant(safe-area-inset-bottom);
  padding-bottom: env(safe-area-inset-bottom);
}

// 顶部装饰背景
.header-decoration {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 12rem;
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  border-bottom-left-radius: 2rem;
  border-bottom-right-radius: 2rem;
  z-index: 0;
}

.main-content {
  position: relative;
  z-index: 1;
  padding: 1.5rem 1.2rem 8rem;
}

// 核心卡片
.hero-card {
  background: #fff;
  border-radius: 1.5rem;
  padding: 2rem 1.5rem;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  margin-bottom: 1.2rem;
  position: relative;
  overflow: hidden;

  .timer-badge {
    position: absolute;
    top: 1rem;
    right: 1.2rem;
    display: flex;
    align-items: center;
    background: #fff;
    border: 1px solid #ff976a;
    padding: 2px 10px;
    border-radius: 20px;
    color: #ff976a;
    font-size: 0.8rem;
    
    .van-icon { margin-right: 4px; }
    
    &.timer-expired {
      border-color: #999;
      color: #999;
    }
  }

  .amount-section {
    margin: 1.5rem 0;
    
    .amount-label {
      font-size: 0.9rem;
      color: #8a8a8e;
      margin-bottom: 0.5rem;
    }
    
    .amount-value {
      color: #2c3e50;
      display: flex;
      justify-content: center;
      align-items: baseline;
      
      .integer { font-size: 3rem; font-weight: 800; font-family: 'PingFang SC', sans-serif; }
      .unit { font-size: 1.2rem; margin-left: 2px; }
    }

    .amount-tip {
      margin-top: 0.35rem;
      font-size: 0.75rem;
      color: rgba(44, 62, 80, 0.62);
      line-height: 1.4;
    }
  }

  .order-info-mini {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    
    .order-no { font-size: 0.8rem; color: #9B9B9B; }
    .copy-btn {
      font-size: 0.75rem;
      color: #e74c3c;
      background: #fff5f5;
      padding: 1px 8px;
      border-radius: 4px;
      font-weight: 500;
      cursor: pointer;
    }
  }
}

.section-card {
  background: #fff;
  border-radius: 1.2rem;
  padding: 1.2rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.03);
  margin-bottom: 1rem;
  
  .section-title {
    font-size: 1.05rem;
    font-weight: 700;
    color: #2c3e50;
    margin-bottom: 1.2rem;
    padding-left: 10px;
    border-left: 4px solid #e74c3c;
  }
}

// 详情列表
.detail-list {
  .detail-item {
    display: flex;
    justify-content: space-between;
    padding: 0.6rem 0;
    font-size: 0.9rem;
    color: #636e72;
    
    .label .van-icon { margin-left: 4px; color: #b2bec3; }
    .value { color: #2d3436; font-weight: 500; }
    
    &.total-item {
      margin-top: 0.5rem;
      padding-top: 1rem;
      border-top: 1px dashed #dfe6e9;
      color: #2d3436;
      font-weight: 700;
      
      .value { font-size: 1.1rem; color: #e74c3c; }
    }
  }
}

// 支付方式
.payment-methods {
  .pay-method-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem;
    border: 1px solid #f1f2f6;
    border-radius: 1rem;
    margin-bottom: 0.8rem;
    transition: all 0.2s ease;
    
    &:last-child { margin-bottom: 0; }
    
    &.item-active {
      border-color: #fb8888;
      background: #fffafa;
    }
    
    &.item-disabled {
      opacity: 0.5;
      pointer-events: none;
      filter: grayscale(1);
    }
    
    .method-left {
      display: flex;
      align-items: center;
      
      .icon-wrapper {
        width: 2.8rem;
        height: 2.8rem;
        border-radius: 0.8rem;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;
        font-size: 1.5rem;
        
        &.wx-bg { background: #f2fff2; .van-image { width: 1.8rem; height: 1.8rem; } }
        &.card-bg { background: #fff1f0; color: #e74c3c; }
      }
      
      .method-info {
        .method-name { 
          font-size: 1rem; 
          font-weight: 600; 
          color: #2d3436;
          
          .no-card-tip {
            font-size: 0.75rem;
            color: #999;
            font-weight: normal;
            margin-left: 4px;
          }
        }
      }
    }

    .method-right {
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
}

// 底部栏
.bottom-bar-wrapper {
  position: fixed;
  bottom: 1rem;
  left: 1.2rem;
  right: 1.2rem;
  z-index: 100;
  
  .bottom-bar {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border-radius: 1.5rem;
    padding: 0.8rem 1.2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(255, 255, 255, 0.4);
  }
  
  .price-summary {
    .summary-row {
      font-size: 0.8rem;
      color: #636e72;
      margin-bottom: 2px;
      .value { 
        font-weight: 600; color: #2d3436; margin-left: 4px;
        .num { color: #e74c3c; font-weight: 800; font-size: 1.1rem; }
      }
      
      &.highlight .value { color: #e74c3c; font-size: 1.1rem; }

      &.error-row {
        .error-text { color: #e74c3c; font-weight: 700; font-size: 0.85rem; }
      }
    }
  }
  
  .pay-submit-btn {
    background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
    color: #fff;
    border: none;
    padding: 0 1.8rem;
    height: 3.2rem;
    border-radius: 1.2rem;
    font-size: 1rem;
    font-weight: 700;
    box-shadow: 0 6px 20px rgba(231, 76, 60, 0.3);
    transition: all 0.3s;
    
    &:active { transform: scale(0.95); opacity: 0.9; }
    
    &:disabled {
      background: #dfe6e9;
      color: #b2bec3;
      box-shadow: none;
    }
    
    &.btn-secondary {
      background: #f1f2f6;
      color: #9b9b9b;
      box-shadow: none;
    }
  }
}

// 弹窗样式
.card-selector-sheet {
  background: #f6f8fb;
  border-radius: 2rem 2rem 0 0;
  overflow: hidden;

  .sheet-content {
    display: flex;
    flex-direction: column;
    height: 75vh;
  }
  
  .card-list {
    flex: 1;
    overflow-y: auto;
    padding: 1.2rem;
  }

  .card-tile {
    background: #fff;
    border-radius: 1.2rem;
    padding: 0.8rem 1rem;
    margin-bottom: 0.8rem;
    border: 2px solid transparent;
    transition: background-color 0.2s, border-color 0.2s;
    
    &.tile-selected {
      border-color: #fb8888;
      background: #fffafa;
    }
    
    &.tile-disabled {
      opacity: 0.6;
      filter: grayscale(0.8);
    }

    // 高亮状态：提示用户普通补差价票数不足，需要继续选择
    &.tile-highlight {
      border-color: #ff6b35;
      border-width: 2px;
      background: linear-gradient(135deg, #fff8f5 0%, #ffede6 100%);
      box-shadow: 0 4px 16px rgba(255, 107, 53, 0.25);
      animation: highlight-pulse 1.5s ease-in-out infinite;
      position: relative;
      
      // 高亮提示角标
      &::after {
        content: '请选择';
        position: absolute;
        top: -8px;
        right: 12px;
        background: linear-gradient(135deg, #ff6b35 0%, #e74c3c 100%);
        color: #fff;
        font-size: 0.65rem;
        padding: 2px 8px;
        border-radius: 8px;
        font-weight: 600;
      }
    }
    
    .tile-header {
      display: flex;
      align-items: center;
      margin-bottom: 0.6rem;
      
      .card-type-icon {
        width: 1.8rem;
        height: 1.8rem;
        background: #fff1f0;
        color: #e74c3c;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 1rem;
        margin-right: 0.6rem;
      }
      
      .card-title-box {
        flex: 1;
        .card-name { font-weight: 700; color: #2d3436; font-size: 0.9rem; }
        .card-meta {
          display: flex;
          align-items: baseline;
          margin-top: 2px;
          gap: 6px;
          .card-id { font-size: 0.65rem; color: #b2bec3; }
          .expire-info { 
            font-size: 0.6rem; 
            color: #ff976a; 
            padding-left: 6px;
            border-left: 1px solid #e0e0e0;
            white-space: nowrap;
          }
        }
      }
    }
    
    .tile-body {
      .card-info-row {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
      .balance-info {
        .label { font-size: 0.8rem; color: #636e72; }
        .value { font-size: 1rem; color: #e74c3c; font-weight: 700; margin-left: 4px; }
      }
      .usage-display-box {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 6px;
        min-height: 2.2rem;
        .label { font-size: 0.8rem; color: #636e72; font-weight: 500; }
        .usage-value {
          font-size: 0.95rem;
          font-weight: 700;
          color: #0984e3;
          background: #e7f3ff;
          padding: 0.2rem 0.6rem;
          border-radius: 0.4rem;
        }
      }
    }
    
    .tile-footer {
      margin-top: 0.4rem;
      padding-top: 0.4rem;
      border-top: 1px solid #f1f2f6;
      
      .error-msg {
        color: #e74c3c;
        font-size: 0.75rem;
        display: flex;
        align-items: center;
        .van-icon { margin-right: 4px; }
      }
    }
  }

  @keyframes highlight-pulse {
    0%, 100% { box-shadow: 0 0 0 0 rgba(255, 151, 106, 0.4); }
    50% { box-shadow: 0 0 0 6px rgba(255, 151, 106, 0); }
  }
  
  .sheet-footer {
    background: #fff;
    padding: 1.2rem;
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.05);
    
    .sheet-summary {
      text-align: center;
      font-size: 0.9rem;
      color: #636e72;
      margin-bottom: 1rem;
      .num { color: #e74c3c; font-weight: 800; font-size: 1.2rem; }
      
      &.has-error {
        margin-bottom: 0.5rem;
      }

      .summary-line {
        font-size: 0.9rem;
        color: #636e72;
        .num { color: #e74c3c; font-weight: 700; }
        .summary-hint {
          display: block;
          margin-top: 4px;
          font-size: 0.75rem;
          color: #b2bec3;
          font-weight: normal;
          .shortfall-num, .wx-amount { color: #b2bec3; font-weight: 500; }
        }
      }
      .error-tip {
        color: #e74c3c;
        font-size: 0.8rem;
        margin-top: 4px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        .van-icon { margin-right: 4px; }
      }
    }
  }
}

// 工具类按钮
.ui-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:active { transform: scale(0.96); opacity: 0.9; }
  
  &-primary { background: #e74c3c; color: #fff; }
  &-gradient { background: linear-gradient(135deg, #fb8888 0%, #e74c3c 100%); color: #fff; }
  &-large { width: 100%; height: 3.2rem; font-size: 1.05rem; }

  &-disabled {
    background: #dfe6e9 !important;
    color: #b2bec3 !important;
    cursor: not-allowed;
    &:active { transform: none; opacity: 1; }
  }
}

// 加载中 & 空状态
.loading-state {
  height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.empty-state {
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 2rem;
  
  .empty-content {
    width: 100%;
    background: #fff;
    border-radius: 2rem;
    padding: 2rem 1.5rem;
    box-shadow: 0 10px 40px rgba(0,0,0,0.05);
  }

  .action-btn-group {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
}

// 高亮呼吸动画
@keyframes highlight-pulse {
  0%, 100% {
    box-shadow: 0 4px 16px rgba(255, 107, 53, 0.25);
    transform: scale(1);
  }
  50% {
    box-shadow: 0 6px 24px rgba(255, 107, 53, 0.4);
    transform: scale(1.01);
  }
}
</style>
